/* global I18n */

import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'

import delay from 'lib/utils/delay'
import Bugsnag from '~/scenes/bugsnag'

import webcamLogic from './logic'

export default kea({
  connect: {
    props: [
      webcamLogic,
      [
        'stream',
        'uiWidth',
        'uiHeight',
        'inputWidth',
        'inputHeight',
        'previewImage',
        'cropSize',
        'streaming',
        'originalInputWidth',
        'originalInputHeight',
        'orientation'
      ]
    ],
    actions: [
      webcamLogic,
      [
        'initStream',
        'setUiSize',
        'setInputSize',
        'activeStreaming',
        'setPreviewImage',
        'setLargeImage',
        'setCropSize',
        'adjustView',
        'takePicture',
        'uploadPicture',
        'startWebcam',
        'setCounterValue',
        'pauseWebcam',
        'initUserMedia',
        'setError',
        'activeWebcam',
        'resetCam',
        'initUI',
        'setOriginInputSize',
        'setOrientation',
        'blockActions',
        'enableActions'
      ]
    ]
  },

  takeLatest: ({ actions, workers }) => ({
    [actions.initUI]: function * () {
      const { setUiSize, setOrientation } = this.actions
      const streamSquareSize =
        Math.min(window.innerWidth, window.innerHeight) -
        (window.innerWidth > 600 && window.innerHeight > 600 ? 300 : 100)
      yield put(setUiSize(streamSquareSize, streamSquareSize))
      const mql = window.matchMedia('(orientation: portrait)')
      if (mql.matches) {
        yield put(setOrientation('portrait'))
      } else {
        // Landscape orientation
        yield put(setOrientation('landscape'))
      }
    },
    [actions.initUserMedia]: function * () {
      const { initUI, activeWebcam, setError, blockActions, enableActions } = this.actions
      yield put(initUI())
      const video = document.getElementById('video-webcam')
      const streamConstraints = {
        audio: false,
        video: { facingMode: 'user' }
      }
      try {
        const stream = yield navigator.mediaDevices.getUserMedia(streamConstraints)
        video.srcObject = stream
        yield put(activeWebcam(stream))
        yield put(enableActions())
      } catch (error) {
        if (error.name && error.name === 'NotAllowedError') {
          yield put(blockActions())
          yield put(setError(I18n.t('profiles.photos.webcam.blocked_webcam_message')))
        } else {
          yield put(setError(I18n.t('modals.error_text')))
          Bugsnag.notify(error)
        }
      }
    },
    [actions.initStream]: function * () {
      const { setInputSize, setOriginInputSize, activeStreaming, setCropSize, setCounterValue, setUiSize } =
        this.actions

      const video = document.getElementById('video-webcam')
      const streaming = yield this.get('streaming')
      const cropSize = yield this.get('cropSize')
      const inputWidth = yield this.get('inputWidth')
      const inputHeight = yield this.get('inputHeight')
      yield put(setCounterValue(null))
      if (!streaming) {
        // crop 720 X 720 unless the vid input is less than that
        if (cropSize > video.videoWidth || cropSize > video.videoHeight) {
          yield put(setCropSize(Math.min(video.videoHeight || inputHeight, video.videoWidth || inputWidth)))
        }

        const streamSquareSize =
          Math.min(window.innerWidth, window.innerHeight) -
          (window.innerWidth > 600 && window.innerHeight > 600 ? 300 : 100)

        const zoomRatio = Math.min(video.videoWidth, video.videoHeight) / streamSquareSize
        yield put(setOriginInputSize(video.videoWidth, video.videoHeight))
        yield put(setInputSize(video.videoWidth / zoomRatio, video.videoHeight / zoomRatio))
        yield put(setUiSize(streamSquareSize, streamSquareSize))
        yield put(activeStreaming())
      }
    },
    [actions.adjustView]: function * () {
      const { setInputSize, initStream, setUiSize, initUserMedia, resetCam } = this.actions
      const video = document.getElementById('video-webcam')

      const originalInputWidth = yield this.get('originalInputWidth')
      const originalInputHeight = yield this.get('originalInputHeight')
      const stream = yield this.get('stream')
      const orientation = yield this.get('orientation')

      const mql = window.matchMedia('(orientation: portrait)')

      if (
        ((mql.matches && orientation === 'landscape') || (!mql.matches && orientation === 'portrait')) &&
        (video.videoWidth !== originalInputWidth || video.videoHeight !== originalInputHeight)
      ) {
        if (stream) {
          const videoTracks = stream.getVideoTracks()
          videoTracks[0].stop()
          yield put(resetCam())
          yield put(initUserMedia())
          yield put(initStream())
        }
      } else {
        const streamSquareSize =
          Math.min(window.innerWidth, window.innerHeight) -
          (window.innerWidth > 600 && window.innerHeight > 600 ? 300 : 100)
        yield put(setUiSize(streamSquareSize, streamSquareSize))
        const zoomRatio = Math.min(video.videoWidth, video.videoHeight) / streamSquareSize

        yield put(setInputSize(video.videoWidth / zoomRatio, video.videoHeight / zoomRatio))
      }
    },
    [actions.takePicture]: function * () {
      const { setPreviewImage, setLargeImage, pauseWebcam } = this.actions
      const originalInputWidth = yield this.get('originalInputWidth')
      const originalInputHeight = yield this.get('originalInputHeight')

      yield call(workers.startCountAnimation)

      const previewCanvas = document.getElementById('preview-canvas')
      const originalImageCanvas = document.getElementById('original-image-canvas')

      const video = document.getElementById('video-webcam')
      const previewContext = previewCanvas.getContext('2d')

      previewCanvas.width = originalInputWidth
      previewCanvas.height = originalInputHeight
      previewCanvas.imageSmoothingEnabled = false
      previewContext.drawImage(video, 0, 0, originalInputWidth, originalInputHeight)

      const previewImage = previewCanvas.toDataURL('image/png')

      const originalContext = originalImageCanvas.getContext('2d')
      originalContext.imageSmoothingEnabled = false
      originalImageCanvas.width = originalInputWidth
      originalImageCanvas.height = originalInputHeight

      originalContext.translate(originalInputWidth, 0)
      originalContext.scale(-1, 1)
      originalContext.drawImage(video, 0, 0, originalInputWidth, originalInputHeight)

      const largeImage = originalImageCanvas.toDataURL('image/png')

      yield put(setPreviewImage(previewImage))
      yield put(setLargeImage(largeImage))
      yield put(pauseWebcam())
    },
    [actions.pauseWebcam]: function * () {
      const stream = yield this.get('stream')
      if (stream) {
        const videoTracks = stream.getVideoTracks()
        videoTracks[0].stop()
      }
    },
    [actions.startWebcam]: function * () {
      const stream = yield this.get('stream')
      const { setPreviewImage, initUserMedia } = this.actions
      if (stream) {
        yield put(setPreviewImage(null))
        yield put(initUserMedia())
      }
    },
    [actions.uploadPicture]: function * (action) {
      const cropSize = yield this.get('cropSize')
      const originalInputWidth = yield this.get('originalInputWidth')
      const originalInputHeight = yield this.get('originalInputHeight')

      const canvas = document.getElementById('original-image-canvas')
      const uploadCanvas = document.getElementById('upload-image-canvas')
      const uploadContext = uploadCanvas.getContext('2d')

      uploadCanvas.width = cropSize
      uploadCanvas.height = cropSize

      const startCropXY = [(originalInputWidth - cropSize) / 2, (originalInputHeight - cropSize) / 2]

      uploadContext.translate(cropSize, 0)
      uploadContext.scale(-1, 1)
      uploadContext.drawImage(canvas, startCropXY[0], startCropXY[1], cropSize, cropSize, 0, 0, cropSize, cropSize)
      action.payload.uploadFunction(uploadCanvas.toDataURL('image/' + 'jpeg', 90 / 100))
    }
  }),

  workers: {
    * startCountAnimation () {
      const { setCounterValue } = this.actions

      yield put(setCounterValue(3))
      yield delay(800)
      yield put(setCounterValue(2))
      yield delay(800)
      yield put(setCounterValue(1))
      yield delay(800)
      yield put(setCounterValue(null))
    }
  }
})
